import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import i18n from "@/i18n";
import { api, queryString } from "@/lib/api";
import type { Page } from "@/lib/types";

const PAGE_SIZE = 25;

/** Paginated list query with page state and status/filter params. */
export function usePaged<T>(
  key: unknown[],
  path: string,
  params: Record<string, string | number | undefined> = {},
) {
  const [page, setPage] = useState(0);

  const query = useQuery({
    queryKey: [...key, { page, params }],
    queryFn: () =>
      api.get<Page<T>>(
        `${path}${queryString({
          ...params,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        })}`,
      ),
    placeholderData: keepPreviousData,
  });

  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return {
    query,
    items: query.data?.items ?? [],
    total,
    page,
    pageCount,
    setPage,
    resetPage: () => setPage(0),
  };
}

export function formatMinor(minor: number, currency: string): string {
  return `${formatMinorAmount(minor)} ${currency}`;
}

/** The number part of `formatMinor`, for a place that names the currency once
 * already (a card with an «AED» badge). */
export function formatMinorAmount(minor: number): string {
  return (minor / 100).toLocaleString(i18n.language, {
    minimumFractionDigits: 2,
  });
}

/** "12.50" -> 1250 minor units; 0 for empty, null for an invalid / negative number. */
export function toMinor(major: string): number | null {
  const s = major.trim();
  if (!s) return 0;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

/** minor units -> a plain "12.50" string for a numeric <input> value. */
export function minorToInput(minor: number | null | undefined): string {
  if (!minor) return "";
  return (minor / 100).toFixed(2);
}
